/**
 * Two-Factor Authentication Service
 *
 * Handles all API communication for the 2FA challenge flow including
 * verification, resending codes, and switching between methods.
 */
class TwoFactorService {
    /**
     * Verify a 2FA response (code, approval, etc.)
     *
     * @param {string} method - The 2FA method (email, totp, webauthn, duo, gifty_app)
     * @param {object} endpoint - Endpoint to submit verification to
     * @param {{transaction_id: string}} payload - Verification payload
     * @param {string} payload.code - Code for code-based methods
     * @param {string} payload.transaction_id - Transaction ID for async methods
     * @returns {Promise<object>} Verification result
     */
    async verify(method, endpoint, payload) {
        try {
            const response = await axios.post(endpoint, {
                method,
                ...payload
            });

            return {
                status: response.status,
                data: response.data
            };
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Resend verification code
     *
     * @param {string} method - The 2FA method
     * @param {object} endpoint - Endpoint to submit resend request to
     * @returns {Promise<object>} Resend result
     */
    async resend(method, endpoint) {
        try {
            const response = await axios.post(endpoint, {
                method
            });

            return {
                status: response.status,
                data: response.data
            };
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Switch to a different 2FA method
     *
     * @param {string} method - The new method to switch to
     * @param {object} endpoint - Endpoint to submit switch request to
     * @returns {Promise<object>} Switch result with new method details
     */
    async switch(method, endpoint) {
        try {
            const response = await axios.post(endpoint, {
                method
            });

            return {
                status: response.status,
                data: response.data
            };
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Poll for async method status (Duo, Gifty App)
     *
     * @param {string} method - The 2FA method
     * @param {string} endpoint - Endpoint to poll
     * @param {string} transactionId - Transaction ID to poll
     * @returns {Promise<object>} Poll result
     */
    async poll(method, endpoint, transactionId) {
        return this.verify(method, endpoint, { transaction_id: transactionId });
    }

    /**
     * Handle API errors
     *
     * @param {object} error - Axios error object
     * @returns {object} Normalized error response
     */
    handleError(error) {
        if (error.response === null || error.response === undefined) {
            return {
                status: 0,
                error_type: 'network_error',
                message: 'Network error. Please check your connection.'
            };
        }

        const status = error.response.status;
        const data = error.response.data || {};

        return {
            status,
            error_type: data.error_type || 'unknown',
            message: data.message || 'An error occurred',
            attempts_remaining: data.attempts_remaining,
            retry_after: data.retry_after,
            retry_at: data.retry_at,
            errors: data.errors,
            data
        };
    }

    /**
     * Check if a response indicates success (2xx status)
     *
     * @param {object} result - Response from service method
     * @returns {boolean}
     */
    isSuccess(result) {
        return result.status >= 200 && result.status < 300;
    }

    /**
     * Check if a response indicates an error (4xx/5xx or network error)
     *
     * @param {object} result - Response from service method
     * @returns {boolean}
     */
    isError(result) {
        return result.status === 0 || result.status >= 400;
    }

    /**
     * Check if a response indicates pending state (202 Accepted)
     *
     * @param {object} result - Response from service method
     * @returns {boolean}
     */
    isPending(result) {
        return result.status === 202;
    }

    /**
     * Check if a method supports resending
     *
     * @param {object} method - Method object from API
     * @returns {boolean}
     */
    canResend(method) {
        return method.metadata?.can_resend === true;
    }

    /**
     * Check if a method requires initiation
     *
     * @param {object} method - Method object from API
     * @returns {boolean}
     */
    requiresInitiation(method) {
        return method.requires_initiation === true;
    }

    /**
     * Check if a method is code-based
     *
     * @param {string} methodType - Method type
     * @returns {boolean}
     */
    isCodeBased(methodType) {
        return ['email', 'sms', 'whatsapp', 'phone', 'totp'].includes(methodType);
    }

    /**
     * Check if a method is async (requires polling)
     *
     * @param {string} methodType - Method type
     * @returns {boolean}
     */
    isAsyncMethod(methodType) {
        return ['gifty_app'].includes(methodType);
    }

    /**
     * Check if a method is redirect-based
     *
     * @param {string} methodType - Method type
     * @returns {boolean}
     */
    isRedirectBased(methodType) {
        return ['duo'].includes(methodType);
    }

    /**
     * Check if a method is WebAuthn
     *
     * @param {string} methodType - Method type
     * @returns {boolean}
     */
    isWebAuthn(methodType) {
        return methodType === 'webauthn';
    }

    /**
     * Check if a method is auto-initiated from the backend
     *
     * @param {object} method - Method object from API
     * @returns {boolean}
     */
    isAutoInitiated(method) {
        return method.auto_initiated === true;
    }

    /**
     * Get polling interval for async methods
     *
     * @param {object} method - Method object from API
     * @returns {number} Interval in seconds
     */
    getPollingInterval(method) {
        return method.metadata?.poll_interval_seconds || 2;
    }

    /**
     * Get retry after duration
     *
     * @param {object} method - Method object from API
     * @returns {number} Duration in seconds
     */
    getRetryAfter(method) {
        return method.retry_after || 60;
    }

    /**
     * Get transaction ID from method metadata
     *
     * @param {object} method - Method object from API
     * @returns {string|null}
     */
    getTransactionId(method) {
        return method.metadata?.transaction_id || null;
    }

    /**
     * Get redirect URL from method metadata
     *
     * @param {object} method - Method object from API
     * @returns {string|null}
     */
    getRedirectUrl(method) {
        return method.metadata?.redirect_url || null;
    }

    /**
     * Get instructions from method metadata
     *
     * @param {object} method - Method object from API
     * @returns {string|null}
     */
    getInstructions(method) {
        return method.metadata?.instructions || null;
    }

    /**
     * Get code length for code-based methods
     * @param method
     * @returns {*|number}
     */
    getCodeLength(method) {
        return method.metadata?.code_length || 6;
    }

    /**
     * Get code type for code-based methods
     * @param method
     * @returns {*|string}
     */
    getCodeType(method) {
        return method.metadata?.code_type || 'numeric';
    }

    /**
     * Validate code format (6 digits)
     *
     * @param {string} code - Code to validate
     * @param {number} codeLength - Expected code length
     * @param {string} codeType - Expected code type
     * @returns {boolean}
     */
    isValidCode(code, codeLength, codeType) {
        if (code.length !== codeLength) {
            return false;
        }

        if (codeType === 'numeric') {
            return /^\d+$/.test(code);
        }

        return /^[0-9a-zA-Z]+$/.test(code);
    }

    /**
     * Determine method category for UI rendering
     *
     * @param {object} method - Method object from API
     * @returns {string} Category: 'code', 'async', 'webauthn', 'redirect'
     */
    getMethodCategory(method) {
        if (this.isCodeBased(method.method)) return 'code';
        if (this.isAsyncMethod(method.method)) return 'async';
        if (this.isWebAuthn(method.method)) return 'webauthn';
        if (this.isRedirectBased(method.method)) return 'redirect';
        return 'unknown';
    }
}

export default new TwoFactorService();
